import React, { useState, useEffect, useMemo } from 'react';
import { Trophy, Package, DollarSign, TrendingUp, Zap } from 'lucide-react';

// RCTI Rankings — ranks the drivers in the current view by earnings, parcels onboarded,
// $/parcel, etc. Driven entirely off the already-built RCTI objects (no extra fetch).

const money = (n) => '$' + (Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num   = (n) => (Number(n) || 0).toLocaleString('en-AU');
const qtyOf = (r, type) => (r.lineItems || []).filter(l => l.type === type).reduce((s, l) => s + (Number(l.quantity) || 0), 0);

const HOURS_BASIS_TITLE = {
  reported: 'Agreed hours (Hourly Drivers page)',
  portal: 'Portal shift times the driver clocked',
  mixed: 'Portal shift times, with scanner hours filling days the portal never captured',
  scanner: 'Scanner hours (Driver Activity Summary) — no portal shifts for this driver',
};

const COLS = [
  { key: 'company',  label: 'Driver / Company', align: 'left' },
  { key: 'parcels',  label: 'Parcels',          fmt: r => num(r.parcels) },
  { key: 'express',  label: 'Express',          fmt: r => num(r.express) },
  { key: 'earnings', label: 'Gross (ex GST)',   fmt: r => money(r.earnings) },
  { key: 'hours',    label: 'Gross Hours',      fmt: r => r.hours == null ? '…' : (r.hours ? r.hours.toFixed(1) : '—') },
  { key: 'perHour',  label: '$ / Hour',         fmt: r => r.hours == null ? '…' : (r.hours ? money(r.perHour) : '—') },
  { key: 'perParcel',label: '$ / Parcel',       fmt: r => r.parcels ? money(r.perParcel) : '—' },
  { key: 'lease',    label: 'Van Lease',        fmt: r => r.lease ? '-' + money(r.lease) : '—' },
  { key: 'total',    label: 'Net (inc GST)',    fmt: r => money(r.total) },
];

function Card({ icon, label, value, sub }) {
  return (
    <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.62rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)' }}>{icon}{label}</div>
      <div style={{ fontWeight: 700, fontSize: '1.15rem', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function RctiRankings({ rctis }) {
  // Gross Hours come from the Driver Activity Summary's scanner source (getUpliftWorkbenchData),
  // which captures EVERY driver's worked hours — not just the agreed-hours drivers (gccd.totalHours
  // was 0 for parcel drivers). Fetched for the period of the listed RCTIs. null = still loading.
  const [hoursMap, setHoursMap] = useState(null);
  const periodISO = useMemo(() => ((rctis || []).find(r => r.periodEndISO) || {}).periodEndISO || '', [rctis]);
  useEffect(() => {
    if (!periodISO) { setHoursMap({}); return; }
    let alive = true; setHoursMap(null);
    fetch('/.netlify/functions/gccd-uplift-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getUpliftWorkbenchData', period: periodISO }),
    })
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        const m = {};
        (d.drivers || []).forEach(x => {
          const lg = String(x.driver || '').toUpperCase();
          // getUpliftWorkbenchData.totalHours already resolves agreed → portal → scanner-fill; keep raw
          // scanner hours as a last resort. basis: 'reported' = agreed (Hourly Drivers page), 'portal' =
          // the driver's clocked shifts, 'mixed' = portal with scanner filling uncaptured days, 'scanner'.
          if (lg) m[lg] = { hours: Number(x.totalHours) || Number(x.scannerHours) || 0, basis: String(x.hoursBasis || '') };
        });
        setHoursMap(m);
      })
      .catch(() => { if (alive) setHoursMap({}); });
    return () => { alive = false; };
  }, [periodISO]);

  const rows = useMemo(() => (rctis || [])
    .filter(r => !r.isHistorical && r.invoiceNumber && r.invoiceNumber !== '—')
    .map(r => {
      const parcels = qtyOf(r, 'parcel');
      const earnings = Number(r.grossIncome) || 0;
      const lg = String(r.driverLogin || '').toUpperCase();
      // Agreed hours (Hourly Drivers) first, scanner (Driver Activity Summary) as fallback. null while loading.
      const info = hoursMap ? hoursMap[lg] : null;
      const hours = hoursMap ? (info ? info.hours : (Number(r.gccd?.totalHours) || 0)) : null;
      const hoursBasis = info ? info.basis : (Number(r.gccd?.totalHours) ? 'reported' : '');
      return {
        invoiceNumber: r.invoiceNumber,
        company: r.companyName || r.personalName || r.driverLogin,
        driver: r.driverLogin,
        parcels,
        express: qtyOf(r, 'express'),
        earnings,
        hours,
        hoursBasis,
        perHour: hours ? earnings / hours : 0,
        gst: Number(r.gst) || 0,
        lease: Math.abs(Number(r.deductions?.leaseFee) || 0),
        total: Number(r.total) || 0,
        perParcel: parcels > 0 ? earnings / parcels : 0,
      };
    }), [rctis, hoursMap]);

  const [sortKey, setSortKey] = useState('earnings');
  const [sortDir, setSortDir] = useState('desc');
  const setSort = (k) => { if (sortKey === k) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortKey(k); setSortDir('desc'); } };

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    const cmp = typeof av === 'string' ? String(av).localeCompare(String(bv)) : ((Number(av) || 0) - (Number(bv) || 0));
    return sortDir === 'desc' ? -cmp : cmp;
  }), [rows, sortKey, sortDir]);

  const totals = useMemo(() => rows.reduce((a, r) => ({
    parcels: a.parcels + r.parcels, express: a.express + r.express,
    earnings: a.earnings + r.earnings, total: a.total + r.total,
    hours: a.hours + (r.hours || 0),
  }), { parcels: 0, express: 0, earnings: 0, total: 0, hours: 0 }), [rows]);

  const topEarner   = useMemo(() => [...rows].sort((a, b) => b.earnings - a.earnings)[0], [rows]);
  const topParcels  = useMemo(() => [...rows].sort((a, b) => b.parcels - a.parcels)[0], [rows]);
  const bestPerPcl  = useMemo(() => rows.filter(r => r.parcels >= 50).sort((a, b) => b.perParcel - a.perParcel)[0], [rows]);
  const avgParcels  = rows.length ? totals.parcels / rows.length : 0;

  if (!rows.length) return <div style={{ padding: 24, color: 'var(--hp-dim)' }}>No RCTIs to rank — build the period first (set the period above and click “Build RCTIs from Sheets”). Tip: set Contractor to “All contractors” for the full leaderboard.</div>;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(150px, 100%),1fr))', gap: 10, marginBottom: 14 }}>
        <Card icon={<Trophy size={12} />} label="Drivers" value={num(rows.length)} />
        <Card icon={<Package size={12} />} label="Total Parcels" value={num(totals.parcels)} sub={`avg ${num(Math.round(avgParcels))}/driver`} />
        <Card icon={<Zap size={12} />} label="Total Express" value={num(totals.express)} />
        <Card icon={<DollarSign size={12} />} label="Total Gross (ex GST)" value={money(totals.earnings)} />
        <Card icon={<DollarSign size={12} />} label="Total Net Payout (inc GST)" value={money(totals.total)} />
        <Card icon={<TrendingUp size={12} />} label="Total Gross Hours" value={hoursMap === null ? '…' : num(Math.round(totals.hours))} sub={hoursMap === null ? 'loading…' : `avg ${num(Math.round(rows.length ? totals.hours / rows.length : 0))}/driver`} />
      </div>

      {/* Highlights */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(200px, 100%),1fr))', gap: 10, marginBottom: 16 }}>
        {topEarner && <Card icon={<TrendingUp size={12} color="var(--hp-gold)" />} label="Top Gross Earner" value={topEarner.company} sub={money(topEarner.earnings) + ' gross ex GST'} />}
        {topParcels && <Card icon={<Package size={12} color="var(--hp-gold)" />} label="Most Parcels Onboarded" value={topParcels.company} sub={num(topParcels.parcels) + ' parcels'} />}
        {bestPerPcl && <Card icon={<DollarSign size={12} color="var(--hp-gold)" />} label="Best $/Parcel (50+ pcls)" value={bestPerPcl.company} sub={money(bestPerPcl.perParcel) + ' / parcel'} />}
      </div>

      <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
        Ranked by <strong>{COLS.find(c => c.key === sortKey)?.label || sortKey}</strong> ({sortDir === 'desc' ? 'high→low' : 'low→high'}) — click any column to re-rank.
        {' '}Gross Hours = agreed hours (Hourly Drivers page); <span style={{ color: 'var(--hp-dim)' }}>scanner&nbsp;hours*</span> from the Driver Activity Summary where none entered.
        {hoursMap === null && <span style={{ color: 'var(--hp-gold)' }}> · loading hours…</span>}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--hp-border)' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--hp-dim)', fontSize: '.66rem' }}>#</th>
              {COLS.map(c => (
                <th key={c.key} onClick={() => setSort(c.key)}
                  style={{ padding: '8px 10px', textAlign: c.align || 'right', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', color: sortKey === c.key ? 'var(--hp-gold)' : 'var(--hp-dim)', fontSize: '.66rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  {c.label}{sortKey === c.key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={r.invoiceNumber + r.driver} style={{ borderBottom: '1px solid var(--hp-border)', background: i < 3 ? 'rgba(201,168,76,.06)' : 'transparent' }}>
                <td style={{ padding: '7px 10px', fontWeight: 700, color: i < 3 ? 'var(--hp-gold)' : 'var(--hp-dim)' }}>{i + 1}</td>
                {COLS.map(c => (
                  <td key={c.key} style={{ padding: '7px 10px', textAlign: c.align || 'right', whiteSpace: 'nowrap' }}>
                    {c.key === 'company'
                      ? <div><div style={{ fontWeight: 600 }}>{r.company}</div><div style={{ fontSize: '.66rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{r.driver} · {r.invoiceNumber}</div></div>
                      : c.key === 'hours' && r.hours
                        ? <span title={HOURS_BASIS_TITLE[r.hoursBasis] || HOURS_BASIS_TITLE.scanner} style={{ color: r.hoursBasis === 'portal' || r.hoursBasis === 'reported' ? 'inherit' : 'var(--hp-dim)' }}>{c.fmt(r)}{(r.hoursBasis === 'scanner' || r.hoursBasis === 'mixed') ? '*' : ''}</span>
                        : c.fmt(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
